import { TextareaHTMLAttributes, forwardRef } from 'react';
import { cn } from '../../lib/utils';

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
    label?: string;
    error?: string | { message?: string } | null;
    helper?: string;
    /** Fill the wrapper to 100% width */
    fullWidth?: boolean;
}

/**
 * TextArea — Unified multiline text field with consistent focus ring, error border, and premium styling.
 * Compatible with React Hook Form via `register()` spread.
 *
 * @example
 * <TextArea label="Description" {...register('description')} error={errors.description} />
 */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
    ({ label, error, helper, fullWidth = true, className, id, ...rest }, ref) => {
        const errorMsg = typeof error === 'string' ? error : (error as any)?.message;
        const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

        return (
            <div className={cn('flex flex-col gap-1.5', fullWidth && 'w-full')}>
                {label && (
                    <label htmlFor={inputId} className="text-xs font-bold text-slate-600 select-none">
                        {label}
                    </label>
                )}
                <textarea
                    ref={ref}
                    id={inputId}
                    className={cn(
                        'w-full min-h-[100px] rounded-xl bg-white border text-sm text-slate-900 placeholder:text-slate-400',
                        'transition-all duration-150 outline-none p-3',
                        'focus-visible:ring-4 focus-visible:ring-teal-primary/20 focus-visible:border-teal-primary',
                        errorMsg ? 'border-red-400 focus:ring-red-300' : 'border-slate-200',
                        'disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed',
                        className
                    )}
                    {...rest}
                />
                {errorMsg && (
                    <p id={inputId ? `${inputId}-error` : undefined} className="text-xs font-medium text-red-500 flex items-center gap-1">{errorMsg}</p>
                )}
                {!errorMsg && helper && (
                    <p className="text-xs text-slate-400">{helper}</p>
                )}
            </div>
        );
    }
);

TextArea.displayName = 'TextArea';
